import React from 'react';
import { Users, ChevronRight, Building2, Clock, UserCheck, AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * MissionCard - Composant pour prise de décision rapide sur les missions
 * 
 * Props:
 * - mission: objet mission avec toutes les données
 * - onViewCandidates: callback pour voir les candidats matchés
 * - onViewDetail: callback pour voir le détail de la mission
 * - isAnimatingOut: état d'animation de sortie
 */
const MissionCard = ({ 
  mission, 
  onViewCandidates, 
  onViewDetail,
  isAnimatingOut
}) => {
  
  // === STATUS DISPLAY ===
  const getStatusDisplay = () => {
    const status = mission.status?.toLowerCase();
    
    switch (status) {
      case 'open':
        return { 
          label: 'À STAFFER', 
          color: 'bg-blue-500', 
          textColor: 'text-white',
          icon: AlertCircle
        };
      case 'assigned':
        return { 
          label: 'ASSIGNÉE', 
          color: 'bg-purple-500', 
          textColor: 'text-white',
          icon: UserCheck
        };
      case 'active':
        return { 
          label: 'EN COURS', 
          color: 'bg-green-500', 
          textColor: 'text-white',
          icon: CheckCircle2
        };
      case 'completed':
        return { 
          label: 'TERMINÉE', 
          color: 'bg-gray-400', 
          textColor: 'text-white',
          icon: CheckCircle2
        };
      default:
        return { 
          label: status?.toUpperCase() || 'INCONNU', 
          color: 'bg-gray-300', 
          textColor: 'text-gray-700',
          icon: null
        };
    }
  };

  // === URGENCY SIGNAL ===
  const getUrgencySignal = () => {
    const daysOld = mission.days_old || 0;
    const status = mission.status?.toLowerCase();
    const matchesCount = mission.matches_count || 0;
    
    // Mission ouverte depuis longtemps sans candidat
    if (status === 'open' && daysOld > 7 && matchesCount === 0) {
      return { icon: AlertCircle, label: 'Urgent - Aucun match', color: 'text-red-500' };
    }
    
    // Mission ouverte avec des matchs à traiter
    if (status === 'open' && matchesCount > 0) {
      return { icon: Users, label: `${matchesCount} candidat${matchesCount > 1 ? 's' : ''} dispo`, color: 'text-green-600' };
    }
    
    // Mission active avec employé assigné
    if (status === 'active' && mission.has_assignment) {
      return { icon: UserCheck, label: mission.assigned_employee_name || 'Assigné', color: 'text-purple-600' };
    }
    
    // Mission ouverte récemment
    if (status === 'open' && daysOld <= 3) {
      return { icon: Clock, label: 'Nouvelle', color: 'text-blue-500' };
    }
    
    return null;
  };

  // === TIME SIGNAL ===
  const getTimeSignal = () => {
    const daysOld = mission.days_old || 0;
    
    if (daysOld === 0) return 'Aujourd\'hui';
    if (daysOld === 1) return 'Hier';
    if (daysOld < 7) return `il y a ${daysOld}j`;
    if (daysOld < 30) return `il y a ${Math.floor(daysOld / 7)} sem.`;
    return `il y a ${Math.floor(daysOld / 30)} mois`;
  };

  // === SENIORITY LABEL ===
  const getSeniorityLabel = () => {
    const level = mission.seniority_level?.toLowerCase();
    switch (level) {
      case 'junior': return 'Junior';
      case 'intermediate': return 'Confirmé';
      case 'senior': return 'Senior';
      default: return level || '';
    }
  };

  // === COMPUTED VALUES ===
  const statusDisplay = getStatusDisplay();
  const urgencySignal = getUrgencySignal();
  const timeSignal = getTimeSignal();
  const StatusIcon = statusDisplay.icon;

  return (
    <div 
      className={cn(
        "bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-300",
        isAnimatingOut && "scale-95 opacity-0"
      )}
      data-testid={`mission-card-${mission.id}`}
    >
      {/* === LIGNE 1: STATUT === */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <span className={cn(
          "inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold tracking-wide",
          statusDisplay.color, statusDisplay.textColor
        )}>
          {StatusIcon && <StatusIcon className="h-3.5 w-3.5" />}
          {statusDisplay.label}
        </span>
        <span className="text-xs text-gray-400">{timeSignal}</span>
      </div>

      {/* === LIGNE 2: SIGNAL URGENCE/INFO === */}
      {urgencySignal && (
        <div className="px-4 pb-2">
          <span className={cn("flex items-center gap-1.5 text-xs font-medium", urgencySignal.color)}>
            <urgencySignal.icon className="h-3.5 w-3.5" />
            {urgencySignal.label}
          </span>
        </div>
      )}

      {/* === SÉPARATEUR === */}
      <div className="border-t border-gray-100 mx-4" />

      {/* === ZONE IDENTITÉ MISSION === */}
      <div className="px-4 py-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {mission.role_required || 'Poste non défini'}
        </h3>
        <p className="text-sm text-gray-500">
          {getSeniorityLabel()} • {mission.duration_type === 'full-time' ? 'Temps plein' : 'Temps partiel'}
        </p>
      </div>

      {/* === ZONE CONTEXTE === */}
      <div className="px-4 pb-3 flex items-center gap-4 text-sm">
        <span className="flex items-center gap-1.5 text-gray-600">
          <Building2 className="h-4 w-4 text-gray-400" />
          {mission.client_name || 'Client inconnu'}
        </span>
        {mission.monthly_budget && (
          <span className="flex items-center gap-1.5 text-gray-600 font-medium">
            💰 {mission.monthly_budget}€/mois
          </span>
        )}
      </div>

      {/* === SKILLS (si présents) === */}
      {mission.required_skills?.length > 0 && (
        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          {mission.required_skills.slice(0, 4).map((skill, idx) => (
            <span 
              key={idx}
              className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
            >
              {skill}
            </span>
          ))}
          {mission.required_skills.length > 4 && (
            <span className="px-2 py-0.5 text-gray-400 text-xs">
              +{mission.required_skills.length - 4}
            </span>
          )}
        </div>
      )}

      {/* === SÉPARATEUR === */}
      <div className="border-t border-gray-100 mx-4" />

      {/* === ZONE ACTIONS === */}
      <div className="p-4 flex gap-3">
        {mission.status === 'open' && mission.matches_count > 0 && (
          <button
            onClick={onViewCandidates}
            className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            data-testid={`view-candidates-${mission.id}`}
          >
            <Users className="h-4 w-4" />
            Voir candidats
          </button>
        )}
        <button
          onClick={onViewDetail}
          className={cn(
            "flex items-center justify-center gap-2 font-medium py-3 px-4 rounded-lg transition-colors",
            mission.status === 'open' && mission.matches_count > 0
              ? "flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
              : "flex-1 bg-tp-primary hover:bg-tp-primary-dark text-white"
          )}
          data-testid={`view-detail-${mission.id}`}
        >
          Voir détail
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default MissionCard;
